import React from 'react';
import { FaChartLine, FaFire, FaTrophy } from 'react-icons/fa';

/**
 * Usage Dashboard Widget Component
 * Displays overview of usage statistics
 * Follows Single Responsibility Principle
 * 
 * @param {Object} usageStats - Usage statistics data
 * @param {Object} subscription - Current subscription data
 * @param {Function} onViewDetails - Callback to view detailed stats
 */
const UsageDashboard = ({ 
  usageStats, 
  subscription, 
  onViewDetails 
}) => {
  // Calculate days remaining in billing cycle
  const daysRemaining = subscription?.end_date 
    ? Math.ceil((new Date(subscription.end_date) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  // Get usage color based on percentage
  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  const features = [
    {
      name: 'Quizzes',
      used: usageStats?.quiz_used || 0,
      limit: usageStats?.quiz_limit || 0,
      icon: '📝',
    },
    {
      name: 'Doubts',
      used: usageStats?.doubt_used || 0,
      limit: usageStats?.doubt_limit || 0,
      icon: '❓',
    },
    {
      name: 'Summaries',
      used: usageStats?.summary_used || 0,
      limit: usageStats?.summary_limit || 0,
      icon: '📄',
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <FaChartLine className="text-blue-600" />
            Your Usage This Month
          </h3>
          {subscription && (
            <p className="text-sm text-gray-600 mt-1">
              Resets in {daysRemaining} days
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 text-orange-600">
            <FaFire />
            <span className="text-2xl font-bold">{usageStats?.streak || 0}</span>
          </div>
          <p className="text-xs text-gray-600">day streak</p>
        </div>
      </div>

      {/* Usage Bars */}
      <div className="space-y-4 mb-6">
        {features.map((feature, index) => {
          const percentage = feature.limit > 0 
            ? Math.min((feature.used / feature.limit) * 100, 100) 
            : 0;
          const isUnlimited = feature.limit === -1;

          return (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <span>{feature.icon}</span>
                  {feature.name}
                </span>
                <span className={`text-sm font-semibold ${getUsageColor(percentage)}`}>
                  {feature.used}/{isUnlimited ? '∞' : feature.limit}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    percentage >= 90 ? 'bg-red-500' :
                    percentage >= 70 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: isUnlimited ? '0%' : `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Achievements (if any) */}
      {usageStats?.recent_achievement && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 text-yellow-700">
            <FaTrophy className="text-lg" />
            <span className="text-sm font-semibold">New Achievement!</span>
          </div>
          <p className="text-xs text-yellow-600 mt-1">
            {usageStats.recent_achievement}
          </p>
        </div>
      )}

      {/* View Details Button */}
      <button
        onClick={onViewDetails}
        className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
      >
        View Detailed Stats
      </button>
    </div>
  );
};

export default UsageDashboard;
